import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LionsStripe from '../components/LionsStripe';
import {
  BookOpenIcon,
  CalculatorIcon,
  BeakerIcon,
  GlobeAltIcon,
  LanguageIcon,
  SparklesIcon,
  BuildingLibraryIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const LearningResourcesPage = () => {
  const elaResources = [
    { name: 'Newsela', description: 'Current events and nonfiction texts at multiple reading levels.', url: 'https://www.newsela.com' },
    { name: 'CommonLit', description: 'High-quality fiction and nonfiction passages with comprehension questions.', url: 'https://www.commonlit.org' },
    { name: 'Epic!', description: 'Digital library of books and videos for elementary students.', url: 'https://www.getepic.com' },
    { name: 'Writable', description: 'Digital writing platform for drafting, revising, and teacher feedback.', url: 'https://www.writable.com' },
    { name: 'HMH', description: 'Literacy curriculum and instructional resources (school access required).', url: 'https://www.hmhco.com' },
    { name: 'i-Ready (Reading)', description: 'Personalized reading instruction based on diagnostics.', url: 'https://www.curriculumassociates.com/products/i-ready' },
    { name: 'Starfall', description: 'Early literacy and phonics practice.', url: 'https://www.starfall.com' },
    { name: 'Storyline Online', description: "Children's books read aloud.", url: 'https://www.storylineonline.net' },
    { name: 'Scholastic', description: 'Reading and content-area literacy resources (some login required).', url: 'https://www.scholastic.com' },
    { name: 'Seussville', description: 'Games and activities based on Dr. Seuss books.', url: 'https://www.seussville.com' },
    { name: 'Raz-Kids', description: 'Leveled digital reading books (account required).', url: 'https://www.raz-kids.com' },
    { name: 'Phonics Hero', description: 'Structured phonics instruction. (paid)', url: 'https://www.phonicshero.com' },
    { name: 'Vooks', description: 'Animated storybooks for comprehension. (paid)', url: 'https://www.vooks.com' },
    { name: 'Unite for Literacy', description: 'Free multilingual digital picture books.', url: 'https://www.uniteforliteracy.com' },
    { name: 'Estrellita', description: 'Spanish early literacy program.', url: 'https://www.estrellita.com' }
  ];

  const mathResources = [
    { name: 'Khan Academy', description: 'Free math lessons and practice.', url: 'https://www.khanacademy.org' },
    { name: 'IXL', description: 'Standards-aligned math practice.', url: 'https://www.ixl.com' },
    { name: 'enVision Math', description: 'Conceptual math curriculum resources.', url: 'https://www.savvas.com/envisionmath' },
    { name: 'i-Ready (Math)', description: 'Personalized math instruction.', url: 'https://www.curriculumassociates.com/products/i-ready' },
    { name: 'HappyNumbers', description: 'Adaptive math instruction.', url: 'https://www.happynumbers.com' },
    { name: 'Math Learning Center Apps', description: 'Visual math tools and manipulatives.', url: 'https://www.mathlearningcenter.org/apps' },
    { name: 'ABCya', description: 'Math and logic games.', url: 'https://www.abcya.com' },
    { name: 'JMathPage', description: 'Math explanations and practice.', url: 'https://www.jmathpage.com' },
    { name: 'AdaptedMind', description: 'Interactive math instruction.', url: 'https://www.adaptedmind.com' },
    { name: 'Quizlet', description: 'Flashcards and review games, especially for math.', url: 'https://quizlet.com' }
  ];

  const scienceResources = [
    { name: 'BrainPOP', description: 'Animated videos and quizzes.', url: 'https://www.brainpop.com' },
    { name: 'BrainPOP Jr.', description: 'Elementary science and content learning.', url: 'https://www.brainpopjr.com' },
    { name: 'National Geographic Kids', description: 'Science articles and videos.', url: 'https://kids.nationalgeographic.com' },
    { name: 'National Geographic Education', description: 'Science and geography resources.', url: 'https://education.nationalgeographic.org' },
    { name: 'Switch Zoo', description: 'Interactive animal science games.', url: 'https://switchzoo.com' },
    { name: 'Funbrain', description: 'Educational games across subjects.', url: 'https://www.funbrain.com' },
    { name: 'Science Bob', description: 'Hands-on science experiments.', url: 'https://www.sciencebob.com' },
    { name: 'PBS Kids – Wild Kratts', description: 'Animal and ecosystem science.', url: 'https://pbskids.org/wildkratts' },
    { name: 'ScienceFlix (Scholastic)', description: 'Science texts and videos (login required).', url: 'https://digital.scholastic.com/site/launch/sfx' },
    { name: 'Minecraft Education', description: 'Project-based learning through creativity and coding.', url: 'https://education.minecraft.net' }
  ];

  const socialStudiesResources = [
    { name: 'Civics for All', description: 'NYC civic education resources.', url: 'https://www.civicsforallnyc.org' },
    { name: 'Passport to Social Studies', description: 'NYC DOE social studies curriculum.', url: 'https://www.schools.nyc.gov/learning/subjects/social-studies/passport-to-social-studies' },
    { name: 'PBS LearningMedia', description: 'Videos and lesson plans.', url: 'https://www.pbslearningmedia.org' },
    { name: 'Kids Discover Online', description: 'Informational texts. (paid)', url: 'https://www.kidsdiscover.com/kids-discover-online' },
    { name: 'TrueFlix (Scholastic)', description: 'Nonfiction texts with videos (login required).', url: 'https://digital.scholastic.com/site/launch/tfx' }
  ];

  const languageResources = [
    { name: 'Wakelet', description: 'Curated multilingual collections.', url: 'https://wakelet.com' },
    { name: 'World Stories', description: "Children's stories in many languages.", url: 'https://worldstories.org.uk' },
    { name: 'Spanish Mama', description: 'Resources for learning Spanish.', url: 'https://spanishmama.com' },
    { name: 'Duolingo', description: 'Game-based language learning.', url: 'https://www.duolingo.com' }
  ];

  const generalResources = [
    { name: 'Education.com Games', description: 'Educational games across subjects.', url: 'https://www.education.com/games' },
    { name: 'PBS Kids', description: 'Educational games and videos.', url: 'https://pbskids.org' },
    { name: 'Highlights Kids', description: 'Reading and puzzles.', url: 'https://www.highlightskids.com' },
    { name: 'Wonderopolis', description: 'Daily inquiry-based learning.', url: 'https://wonderopolis.org' },
    { name: 'YouTube Kids', description: 'Child-friendly educational videos.', url: 'https://www.youtubekids.com' },
    { name: 'Calm', description: 'Mindfulness and relaxation tools.', url: 'https://www.calm.com' },
    { name: 'Mindful Schools', description: 'Free mindfulness lessons.', url: 'https://www.mindfulschools.org/free-online-mindfulness-class-for-kids' },
    { name: 'Sesame Street – Caring', description: 'SEL resources.', url: 'https://www.sesamestreet.org/caring' }
  ];

  const museums = [
    { name: 'El Museo del Barrio', address: '1230 Fifth Avenue, New York, NY 10029', url: 'https://www.elmuseo.org' },
    { name: 'Museum of the City of New York', address: '1220 Fifth Avenue, New York, NY 10029', url: 'https://www.mcny.org' },
    { name: 'The Africa Center', address: '1280 Fifth Avenue, New York, NY 10029', url: 'https://www.africacenter.org' },
    { name: "El Barrio's Artspace PS109", address: '215 East 99th Street, New York, NY 10029', url: 'https://www.elbosartspaceps109.org' },
    { name: 'Julia de Burgos Latino Cultural Center', address: '1680 Lexington Avenue, New York, NY 10029', url: 'https://www.juliadeburgos.org' },
    { name: 'Central Park North Woods', address: 'Entrance near 110th Street & Lenox Avenue, New York, NY 10026', url: 'https://www.centralparknyc.org/locations/north-woods' },
    { name: 'Central Park Conservatory Garden', address: 'Entrance at East 105th Street & Fifth Avenue, New York, NY 10029', url: 'https://www.centralparknyc.org/locations/conservatory-garden' },
    { name: 'Cooper Hewitt, Smithsonian Design Museum', address: '2 East 91st Street, New York, NY 10128', url: 'https://www.cooperhewitt.org' },
    { name: 'The Metropolitan Museum of Art', address: '1000 Fifth Avenue, New York, NY 10028', url: 'https://www.metmuseum.org' },
    { name: "The Children's Museum of Manhattan", address: '212 West 83rd Street, New York, NY 10024', url: 'https://cmom.org' },
    { name: 'American Museum of Natural History', address: '200 Central Park West, New York, NY 10024', url: 'https://www.amnh.org' },
    { name: 'Bronx Zoo', address: '2300 Southern Boulevard, Bronx, NY 10460', url: 'https://bronxzoo.com' }
  ];

  const communityOrgs = [
    { name: "Children's Aid", address: 'Located within school building', url: 'https://www.childrensaidnyc.org' },
    { name: 'Union Settlement Association', address: '237 East 104th Street, New York, NY 10029', url: 'https://www.unionsettlement.org' },
    { name: 'Boys Club of New York – East Harlem', address: '142 East 111th Street, New York, NY 10029', url: 'https://www.bcny.org' },
    { name: 'Groove With Me Inc.', address: '1301 Fifth Avenue (at East 110th Street), New York, NY 10029', url: 'https://www.groovewithme.org' },
    { name: 'Child Mind Institute', address: '101 East 56th Street, New York, NY 10022', url: 'https://childmind.org' },
    { name: 'Little Sisters of the Assumption Family Health Service', address: '333 East 115th Street, New York, NY 10029', url: 'https://littlesistersfamily.org' },
    { name: 'Manhattan Community Board 11 (East Harlem)', address: '1664 Park Avenue, Suite 1666, New York, NY 10035', url: 'https://www.nyc.gov/site/manhattancb11' },
    { name: 'District 4 Office', address: '208 East 120th Street, New York, NY 10035', url: 'https://www.schools.nyc.gov/about-us/leadership/districts' }
  ];

  const ResourceCard = ({ resource }) => (
    <a
      href={resource.url}
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-gray-50 rounded-2xl p-6 border-2 border-gray-200 hover:border-secondary hover:bg-white hover:shadow-lg transition-all duration-300 group"
    >
      <h3 className="font-bold text-gray-900 text-lg mb-2 group-hover:text-orange-700 transition-colors">
        {resource.name}
      </h3>
      <p className="text-gray-700 text-sm leading-relaxed mb-3">
        {resource.description || resource.address}
      </p>
      <div className="flex items-center gap-1 text-orange-700 font-semibold text-sm">
        <span>Visit site</span>
        <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </div>
    </a>
  );

  const SectionHeader = ({ icon: Icon, title, color = 'bg-primary' }) => (
    <div className={`${color} p-8 rounded-t-3xl text-white`}>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
          <Icon className="w-7 h-7" />
        </div>
        <h2 className="text-3xl font-bold">{title}</h2>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-accent-light font-sans selection:bg-secondary selection:text-white">
      <Navbar />

      <main id="main-content">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-primary overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-navy-900 to-primary"></div>
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[100px]"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-[100px]"></div>
          </div>

          <div className="max-w-4xl mx-auto px-4 text-center relative z-10 bg-primary rounded-2xl py-1">
            <div className="inline-flex items-center justify-center p-3 bg-white/10 backdrop-blur rounded-full mb-8 border border-white/20">
              <BookOpenIcon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
              Learning <span className="text-secondary">Resources</span>
            </h1>
            <p className="text-xl text-white/80 font-light max-w-2xl mx-auto">
              Educational tools and resources to support student learning at home and in school
            </p>
          </div>
          <LionsStripe className="absolute inset-x-0 bottom-0 z-10" />
        </section>

        {/* Content */}
        <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 -mt-12 relative z-20">
          <div className="max-w-7xl mx-auto space-y-12">

            {/* Reading & Language Arts */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <SectionHeader icon={BookOpenIcon} title="Reading & Language Arts (ELA)" color="bg-blue-600" />
              <div className="p-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {elaResources.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} />
                ))}
              </div>
            </div>

            {/* Mathematics */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <SectionHeader icon={CalculatorIcon} title="Mathematics" color="bg-green-600" />
              <div className="p-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {mathResources.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} />
                ))}
              </div>
            </div>

            {/* Science, STEM & Technology */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <SectionHeader icon={BeakerIcon} title="Science, STEM & Technology" color="bg-purple-600" />
              <div className="p-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {scienceResources.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} />
                ))}
              </div>
            </div>

            {/* Social Studies */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <SectionHeader icon={GlobeAltIcon} title="Social Studies" color="bg-amber-600" />
              <div className="p-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {socialStudiesResources.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} />
                ))}
              </div>
            </div>

            {/* Spanish & Other Languages */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <SectionHeader icon={LanguageIcon} title="Spanish & Other Languages" color="bg-red-600" />
              <div className="p-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {languageResources.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} />
                ))}
              </div>
            </div>

            {/* General Learning, Wellness & Enrichment */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <SectionHeader icon={SparklesIcon} title="General Learning, Wellness & Enrichment" color="bg-pink-600" />
              <div className="p-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {generalResources.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} />
                ))}
              </div>
            </div>

            {/* Museums & Real-Life Educational Experiences */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <SectionHeader icon={BuildingLibraryIcon} title="Museums & Real-Life Educational Experiences (East Harlem & Nearby)" color="bg-teal-600" />
              <div className="p-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {museums.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} />
                ))}
              </div>
            </div>

            {/* Community-Based Organizations */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <SectionHeader icon={UserGroupIcon} title="Community-Based Organizations & Family Services" color="bg-indigo-600" />
              <div className="p-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {communityOrgs.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} />
                ))}
              </div>
            </div>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default LearningResourcesPage;
